import React, { useContext, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Style from "./PaginaContratoStyle";
import { AuthContext } from "../../context/AuthContext";
import { useMessage } from "../../context/MessageContext";
import {
  formatNumber,
  formatDateSystem,
  retornaResposta,
  formatarData,
} from "../../assets/utils";
import {
  FiArrowLeft,
  FiAlertCircle,
  FiClock,
  FiFileText,
  FiDollarSign,
  FiCheckCircle,
  FiTrendingUp,
  FiBarChart2,
  FiPercent,
  FiCalendar,
  FiHash,
  FiUpload,
  FiGift, // Ícone para Golden Box
  FiCheck,
} from "react-icons/fi";
import Loading from "../Loading/Loader";
import { contractService } from "../../Services/contractService";
import CreateReceiptModal from "./CreateReceiptModal";
import { formatDateDisplay } from "../../assets/assets";

const PaginaContrato = () => {
  const { contratoId } = useParams();
  const navigate = useNavigate();
  const { contracts, reloadUserData } = useContext(AuthContext);
  const { showMessage } = useMessage();
  const [contratoData, setContratoData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  useEffect(() => {
    const findContrato = contracts?.find(
      (c) => c.contract.id.toString() === contratoId
    );
    if (findContrato) {
      setContratoData(findContrato);
    }
    setIsLoading(false);
  }, [contratoId, contracts]);

  const handleCancelContract = async () => {
    if (
      !window.confirm(
        "Você tem certeza que deseja cancelar este contrato? Esta ação não pode ser desfeita e o valor não será reembolsado."
      )
    )
      return;

    setIsLoading(true);
    try {
      await contractService.cancelContract(contratoData.contract.id);
      showMessage("Contrato cancelado com sucesso!", "success");
      await reloadUserData();
      navigate(-1);
    } catch (error) {
      console.error("Erro ao cancelar contrato:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Não foi possível cancelar o contrato.";
      showMessage(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReceiptSuccess = async () => {
    await reloadUserData();
  };

  // --- NOVA FUNÇÃO: Redireciona para Caixinha com Params ---
  const handleAddToGoldenBox = () => {
    // Redireciona para /caixinha passando query params para abrir o modal já com este contrato
    navigate(`/caixinha?activateGoldenBox=true&contractId=${contract.id}`);
  };

  if (isLoading) {
    return (
      <Style.LoadingContainer>
        Carregando detalhes do contrato...
      </Style.LoadingContainer>
    );
  }

  if (!contratoData) {
    return (
      <Style.LoadingContainer>Contrato não encontrado.</Style.LoadingContainer>
    );
  }

  const { contract, avaliableToWithdraw } = contratoData;

  const statusInfo = {
    text: retornaResposta(contract.status),
    icon:
      contract.status === 1 ? (
        <FiTrendingUp />
      ) : contract.status === 4 ? (
        <FiClock />
      ) : (
        <FiAlertCircle />
      ),
  };

  const lucroAtualValor =
    contract.totalPrice * (contract.actualValorization / 100);

  return (
    <Style.PageWrapper>
      {isLoading && <Loading load={isLoading} />}
      {showReceiptModal && (
        <CreateReceiptModal
          contractId={contract.id}
          clientId={contract.clientId}
          onClose={() => setShowReceiptModal(false)}
          onSuccess={handleReceiptSuccess}
        />
      )}
      <Style.BackButton onClick={() => navigate(-1)} title="Voltar">
        <FiArrowLeft />
      </Style.BackButton>
      <Style.MainContent>
        <Style.Header>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <Style.Title>
              Contrato <span>#{contract.id}</span>
            </Style.Title>
            <div style={{ display: "flex", gap: 10 }}>
              <Style.StatusBadge status={contract.status}>
                {statusInfo.icon} {statusInfo.text}
              </Style.StatusBadge>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 15,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {/* LÓGICA DO BOTÃO GOLDEN BOX */}
            {contract.goldenBoxActived ? (
              <Style.GoldenBadge title="Este contrato já pertence a uma Caixinha Golden">
                <FiGift /> Caixinha Ativa <FiCheck />
              </Style.GoldenBadge>
            ) : (
              // Só mostra botão de adicionar se o contrato estiver ATIVO (Status 1)
              contract.status === 1 && (
                <Style.GoldenButton onClick={handleAddToGoldenBox}>
                  <FiGift /> Adicionar à Caixinha
                </Style.GoldenButton>
              )
            )}

            {/* BOTÕES DE RECIBO */}
            {contract.receiptUrl ? (
              <Style.ReceiptButton
                href={contract.receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FiFileText /> Ver Recibo
              </Style.ReceiptButton>
            ) : (
              <Style.ReceiptButton
                onClick={() => setShowReceiptModal(true)}
                outline
              >
                <FiUpload /> Anexar Recibo
              </Style.ReceiptButton>
            )}
          </div>
        </Style.Header>

        <Style.Section id="sumario">
          <Style.SectionTitle>Sumário do Contrato</Style.SectionTitle>
          <Style.CardGrid>
            <Style.InfoCard>
              <Style.InfoLabel>
                <FiDollarSign /> Valor Aportado
              </Style.InfoLabel>
              <Style.InfoValue highlight="primary">
                R$ {formatNumber(contract.totalPrice)}
              </Style.InfoValue>
            </Style.InfoCard>
            <Style.InfoCard>
              <Style.InfoLabel>
                <FiTrendingUp /> Valorização Atual
              </Style.InfoLabel>
              <Style.InfoValue>
                {formatNumber(contract.actualValorization)}%
              </Style.InfoValue>
              <Style.InfoSubValue>
                + R$ {formatNumber(lucroAtualValor)}
              </Style.InfoSubValue>
            </Style.InfoCard>
            <Style.InfoCard>
              <Style.InfoLabel>
                <FiBarChart2 /> Teto de Valorização
              </Style.InfoLabel>
              <Style.InfoValue>
                {formatNumber(contract.maxValorization)}%
              </Style.InfoValue>
            </Style.InfoCard>
            <Style.InfoCard>
              <Style.InfoLabel>
                <FiCheckCircle /> Disponível para Saque
              </Style.InfoLabel>
              <Style.InfoValue highlight="success">
                R$ {formatNumber(avaliableToWithdraw || 0)}
              </Style.InfoValue>
            </Style.InfoCard>
          </Style.CardGrid>
        </Style.Section>

        <Style.Section id="detalhes">
          <Style.SectionTitle>Detalhes do Contrato</Style.SectionTitle>
          <Style.CardGrid>
            <Style.InfoCard>
              <Style.InfoLabel>
                <FiHash /> Quantidade
              </Style.InfoLabel>
              <Style.InfoValue>
                {contract.contractQtt} Contrato(s)
              </Style.InfoValue>
            </Style.InfoCard>
            <Style.InfoCard>
              <Style.InfoLabel>
                <FiPercent /> Valor por Contrato
              </Style.InfoLabel>
              <Style.InfoValue>
                R$ {formatNumber(contract.contractUniValue)}
              </Style.InfoValue>
            </Style.InfoCard>
          </Style.CardGrid>
        </Style.Section>

        <Style.Section id="prazos">
          <Style.SectionTitle>Prazos e Datas</Style.SectionTitle>
          <Style.CardGrid>
            <Style.InfoCard>
              <Style.InfoLabel>
                <FiCalendar /> Data da Compra
              </Style.InfoLabel>
              <Style.InfoValue>
                {formatDateSystem(contract.dateCreated)}
              </Style.InfoValue>
            </Style.InfoCard>
            <Style.InfoCard>
              <Style.InfoLabel>
                <FiClock /> Duração
              </Style.InfoLabel>
              <Style.InfoValue>{contract.duration} meses</Style.InfoValue>
            </Style.InfoCard>
            <Style.InfoCard>
              <Style.InfoLabel>
                <FiCalendar /> Início da Valorização
              </Style.InfoLabel>
              <Style.InfoValue>
                {contract.firstValorization
                  ? formatDateDisplay(contract.firstValorization)
                  : "Aguardando Pagamento"}
              </Style.InfoValue>
            </Style.InfoCard>
            <Style.InfoCard>
              <Style.InfoLabel>
                <FiCalendar /> Término Previsto
              </Style.InfoLabel>
              <Style.InfoValue>
                {formatDateSystem(contract.endContractDate)}
              </Style.InfoValue>
            </Style.InfoCard>
          </Style.CardGrid>
        </Style.Section>

        {contract.status === 4 && (
          <Style.FooterActions>
            <Style.ActionButton onClick={handleCancelContract}>
              Cancelar Contrato
            </Style.ActionButton>
          </Style.FooterActions>
        )}
      </Style.MainContent>
    </Style.PageWrapper>
  );
};

export default PaginaContrato;
